import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import {
  UploadCloud, X, Phone, Clock, Loader2, CheckCircle2, AlertCircle,
} from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL } from "../../lib/constants";
import { trackPhoneClick, trackFormSubmit } from "../../lib/gtag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Lakás- és pinceürítés",
  "Építési törmelék elszállítás",
  "Iroda & raktár lomtalanítás",
  "Kert & zöldhulladék elszállítás",
  "Egyéb",
];

const inputCls =
  "w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3.5 text-sm text-[#1E293B] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#16A34A]/40 focus:border-[#16A34A] transition-colors";

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", phone: "", location: "", service: SERVICES[0], message: "" });
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const onDrop = useCallback((accepted) => {
    setFiles((prev) => [...prev, ...accepted].slice(0, 5));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
    maxFiles: 5,
    maxSize: 10 * 1024 * 1024,
  });

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("phone", form.phone);
      data.append("location", form.location);
      data.append("service", form.service);
      data.append("message", form.message);
      files.forEach((f) => data.append("photos", f));
      await axios.post(`${API}/quote`, data);
      trackFormSubmit();
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err.response?.data?.detail ||
          "Nem sikerült elküldeni az űrlapot. Kérjük, próbálja újra, vagy hívjon minket telefonon!"
      );
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <section id="kapcsolat" data-testid="contact-section" className="py-24 lg:py-32 bg-white border-t border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#16A34A] mb-4">03 / Kapcsolat</p>
          <h2 className="font-display font-black tracking-tighter text-[#1E293B] text-4xl sm:text-5xl">
            Kérjen díjmentes ajánlatot.
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#64748B] leading-relaxed">
            Töltse ki az űrlapot, csatoljon fotót a lomról, és 24 órán belül fix áras ajánlatot küldünk.
            Vagy hívjon minket közvetlenül:
          </p>
          <a
            data-testid="contact-phone-link"
            href={PHONE_TEL}
            onClick={trackPhoneClick}
            className="mt-6 inline-flex items-center gap-3 bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-8 rounded-full text-lg shadow-lg shadow-green-600/30 animate-pulse transition-colors"
          >
            <Phone className="w-5 h-5" />
            Hívás Most: {PHONE_DISPLAY}
          </a>
          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-3 text-sm text-[#475569]">
              <Clock className="w-5 h-5 text-[#16A34A]" />
              Nyitvatartás: <strong className="text-[#1E293B]">H–V: 08:00 – 20:00</strong>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-7"
        >
          {status === "success" ? (
            <div
              data-testid="form-success"
              className="bg-[#F8FAFC] border border-green-200 rounded-[2rem] p-10 lg:p-14 text-center h-full flex flex-col items-center justify-center"
            >
              <CheckCircle2 className="w-16 h-16 text-[#16A34A] mb-6" />
              <h3 className="font-display font-black text-[#1E293B] text-2xl lg:text-3xl tracking-tight mb-3">
                Köszönjük, megkaptuk az ajánlatkérését!
              </h3>
              <p className="text-[#64748B] text-base max-w-md">
                Kollégánk 24 órán belül felveszi Önnel a kapcsolatot a megadott telefonszámon.
                Sürgős esetben hívjon minket:{" "}
                <a href={PHONE_TEL} onClick={trackPhoneClick} className="text-[#16A34A] font-bold" data-testid="success-phone-link">
                  {PHONE_DISPLAY}
                </a>
              </p>
              <button
                data-testid="form-reset-button"
                onClick={() => { setStatus("idle"); setFiles([]); setForm({ name: "", phone: "", location: "", service: SERVICES[0], message: "" }); }}
                className="mt-8 text-sm font-semibold text-[#16A34A] hover:text-[#15803d] transition-colors"
              >
                Új ajánlatkérés küldése
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="quote-form"
              className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-[2rem] p-8 lg:p-10 space-y-5"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="form-name" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2">Név *</label>
                  <input
                    id="form-name"
                    data-testid="form-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Kovács János"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="form-phone" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2">Telefonszám *</label>
                  <input
                    id="form-phone"
                    data-testid="form-phone-input"
                    required
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+36 30 123 4567"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="form-location" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2">Település / Kerület *</label>
                  <input
                    id="form-location"
                    data-testid="form-location-input"
                    required
                    value={form.location}
                    onChange={set("location")}
                    placeholder="pl. Budapest, XIII. kerület"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="form-service" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2">Szolgáltatás típusa *</label>
                  <select
                    id="form-service"
                    data-testid="form-service-select"
                    value={form.service}
                    onChange={set("service")}
                    className={inputCls}
                  >
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2">
                  Fotó feltöltése a lomról (opcionális, a pontosabb árajánlathoz)
                </label>
                <div
                  {...getRootProps()}
                  data-testid="photo-dropzone"
                  className={`border-2 border-dashed rounded-2xl px-6 py-10 text-center cursor-pointer transition-colors ${
                    isDragActive ? "border-[#16A34A] bg-green-50" : "border-[#CBD5E1] bg-white hover:border-green-400"
                  }`}
                >
                  <input {...getInputProps()} data-testid="photo-input" />
                  <UploadCloud className={`w-10 h-10 mx-auto mb-3 ${isDragActive ? "text-[#16A34A]" : "text-[#94A3B8]"}`} />
                  <p className="text-sm font-semibold text-[#1E293B]">
                    Húzza ide a képeket vagy kattintson a tallózáshoz
                  </p>
                  <p className="text-xs text-[#94A3B8] mt-1">JPG, PNG vagy WebP • Max 5 kép • Max 10 MB / kép</p>
                </div>
                {files.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4" data-testid="photo-preview-grid">
                    {files.map((file, i) => (
                      <div key={`${file.name}-${i}`} className="relative group rounded-xl overflow-hidden border border-[#E2E8F0] aspect-square">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-full h-full object-cover"
                          data-testid={`photo-preview-${i}`}
                        />
                        <button
                          type="button"
                          data-testid={`photo-remove-${i}`}
                          onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#1E293B]/80 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                          aria-label="Kép törlése"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="form-message" className="block text-xs font-bold uppercase tracking-wider text-[#475569] mb-2">Rövid leírás / Üzenet</label>
                <textarea
                  id="form-message"
                  data-testid="form-message-textarea"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="pl. 2. emeleti bútorozott lakás kiürítése, lift van..."
                  className={`${inputCls} resize-none`}
                />
              </div>

              {status === "error" && (
                <div data-testid="form-error" className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                  <AlertCircle className="w-5 h-5 shrink-0" />
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                data-testid="form-submit-button"
                disabled={status === "loading"}
                className="w-full bg-[#16A34A] hover:bg-[#15803d] disabled:opacity-70 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-full text-base flex items-center justify-center gap-3 shadow-lg shadow-green-600/30 transition-colors"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Küldés folyamatban...
                  </>
                ) : (
                  "Kérem a díjmentes ajánlatot"
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
